import {t} from "@lingui/core/macro";
import {Plural, Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {redirect} from "@repo/urls";

import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";

import {strings} from "./strings.ts";
import {SCHEDULE_USER_DELETION_MUTATION} from "./user-deletion.ts";
import WithHoverTip from "./with-hover-tip.tsx";

// sync-start:delete-user-waiting-period 409675594 https://github.com/Khan/webapp/blob/master/services/users/user_deletion/initiate.go
const DAYS_UNTIL_DELETION = 1;
// sync-end:delete-user-waiting-period

type OwnProps = {
    badgeCount: number;
    fromOwnSettingsPage: boolean;
    isModifyingChild: boolean;
    onClose: () => unknown;
    points: number;
    kaid: string;
    isChild: boolean;
    nickname: string;
};

type Props = OwnProps & WithApolloProps;

type State = {
    // We temporarily disable the buttons while deleting the user
    loading: boolean;
};

class DeleteAccountModal extends React.Component<Props, State> {
    // @ts-expect-error - TS2564 - Property '_mounted' has no initializer and is not definitely assigned in the constructor.
    _mounted: boolean;
    state: State = {loading: false};

    componentDidMount() {
        this._mounted = true;
    }

    componentWillUnmount() {
        this._mounted = false;
    }

    // This fetches a deletion code and then calls to delete the user
    handleClickDelete: () => void = () => {
        const {fromOwnSettingsPage, isModifyingChild, kaid, gqlClient} =
            this.props;
        this.setState({loading: true});

        gqlClient
            .mutate(SCHEDULE_USER_DELETION_MUTATION, {
                variables: {targetKaid: kaid},
            })
            .then((result) => {
                if (!this._mounted) {
                    return;
                }
                if (
                    result.data?.scheduleUserDeletion &&
                    !result.data.scheduleUserDeletion.error
                ) {
                    if (isModifyingChild) {
                        // Child deleted. If the actor came from their own settings
                        // page, take them back there. Otherwise, take them
                        // to the student list.
                        if (fromOwnSettingsPage) {
                            redirect("/settings/account");
                        } else {
                            redirect("/teacher/students");
                        }
                    } else {
                        // Logout just to be safe (to clear third party auth
                        // stuff?) and continue to homepage.
                        redirect("/logout?continue=%2F");
                    }
                } else {
                    // TODO(benkomalo/kamens): handle this failure in a nicer way
                    // eslint-disable-next-line no-alert
                    alert(
                        t`We ran into a problem trying to delete your account. If this happens repeatedly, please let us know via 'Report a Problem' at the bottom of this page so we can help.`,
                    );
                    this.setState({loading: false});
                }
            })
            .catch(() => {
                if (!this._mounted) {
                    return;
                }
                // TODO(benkomalo/kamens): handle this failure in a nicer way
                // eslint-disable-next-line no-alert
                alert(
                    t`We ran into a problem trying to delete your account. If this happens repeatedly, please let us know via 'Report a Problem' at the bottom of this page so we can help.`,
                );
                this.setState({loading: false});
            });
    };

    render(): React.ReactNode {
        const {isChild, nickname, badgeCount, points} = this.props;

        const deleteWarning = (
            <Trans>
                Once you click Delete, your account and all of its history will
                automatically be deleted directly after this waiting period.{" "}
                <b>You don't have to do anything else.</b>
            </Trans>
        );

        return (
            <OnePaneDialog
                title={t`Delete account`}
                content={
                    <View>
                        <Body style={styles.topText}>
                            <Trans>
                                You are about to delete the account{" "}
                                <strong>{nickname}</strong>:
                            </Trans>
                        </Body>
                        {points > 0 && (
                            <View>
                                <Body style={styles.topHeader}>
                                    {t`This will discard:`}
                                </Body>
                                <ul className={css(styles.list)}>
                                    {badgeCount > 0 && (
                                        <li>
                                            <Body>
                                                {strings.numBadges(badgeCount)}
                                            </Body>
                                        </li>
                                    )}
                                    <li>
                                        <Body>
                                            {t`all video and skill progress`}
                                        </Body>
                                    </li>
                                    {!isChild && (
                                        <li>
                                            <Body>
                                                {t`all discussion posts and votes`}
                                            </Body>
                                        </li>
                                    )}
                                    <li>
                                        <Body>
                                            {t`all computer science programs`}
                                        </Body>
                                    </li>
                                </ul>
                            </View>
                        )}
                        {isChild ? (
                            <Body style={styles.bottomText}>
                                <Trans>
                                    Heads up! This will{" "}
                                    <strong>PERMANENTLY DELETE</strong> your
                                    child’s account. This{" "}
                                    <strong>CANNOT</strong> be undone.
                                </Trans>
                            </Body>
                        ) : (
                            <Body style={styles.bottomText}>
                                <Plural
                                    value={DAYS_UNTIL_DELETION}
                                    one={
                                        <Trans>
                                            Your account will be deleted # day
                                            from now. If you log in with this
                                            account before then, it will not be
                                            deleted. After # day, deleting this
                                            account <strong>CANNOT</strong> be
                                            undone.{" "}
                                            <WithHoverTip
                                                tipContents={
                                                    <React.Fragment>
                                                        {t`In order to protect students from accidentally deleting their data, we wait a day before fully deleting any account.`}
                                                        <br />
                                                        <br />
                                                        {deleteWarning}
                                                    </React.Fragment>
                                                }
                                                position={{
                                                    my: "bottom center",
                                                    at: "top center",
                                                }}
                                            >
                                                {/* eslint-disable-next-line jsx-a11y/anchor-ambiguous-text -- TODO: Address a11y error */}
                                                <a
                                                    href="/"
                                                    id="learn-about-delete"
                                                    onClick={(
                                                        e: React.MouseEvent,
                                                    ) => e.preventDefault()}
                                                >
                                                    Learn more.
                                                </a>
                                            </WithHoverTip>
                                        </Trans>
                                    }
                                    other={
                                        <Trans>
                                            Your account will be deleted # days
                                            from now. If you log in with this
                                            account before then, it will not be
                                            deleted. After # days, deleting this
                                            account <strong>CANNOT</strong> be
                                            undone.{" "}
                                            <WithHoverTip
                                                tipContents={
                                                    <React.Fragment>
                                                        {t`In order to protect students from accidentally deleting their data, we wait a day before fully deleting any account.`}
                                                        <br />
                                                        <br />
                                                        {deleteWarning}
                                                    </React.Fragment>
                                                }
                                                position={{
                                                    my: "bottom center",
                                                    at: "top center",
                                                }}
                                            >
                                                {/* eslint-disable-next-line jsx-a11y/anchor-ambiguous-text -- TODO: Address a11y error */}
                                                <a
                                                    href="/"
                                                    id="learn-about-delete"
                                                    onClick={(
                                                        e: React.MouseEvent,
                                                    ) => e.preventDefault()}
                                                >
                                                    Learn more.
                                                </a>
                                            </WithHoverTip>
                                        </Trans>
                                    }
                                />
                            </Body>
                        )}
                    </View>
                }
                footer={
                    <View style={styles.row}>
                        <Button
                            testId="cancel"
                            onClick={this.props.onClose}
                            kind="secondary"
                            actionType="destructive"
                            disabled={this.state.loading}
                            style={styles.modalFooterButton}
                        >
                            {t`Cancel`}
                        </Button>
                        <Button
                            testId="delete"
                            onClick={this.handleClickDelete}
                            actionType="destructive"
                            disabled={this.state.loading}
                        >
                            {t`Delete`}
                        </Button>
                    </View>
                }
                style={styles.modal}
            />
        );
    }
}

type ExportProps = WithoutApollo<
    JSX.LibraryManagedAttributes<
        typeof DeleteAccountModal,
        React.ComponentProps<typeof DeleteAccountModal>
    >
>;
export default withApollo(
    DeleteAccountModal,
) as React.ComponentType<ExportProps>;

const styles = StyleSheet.create({
    list: {
        listStyle: "disc",
        paddingLeft: spacing.medium_16,
    },
    modal: {
        width: 514,
    },
    modalFooterButton: {
        marginRight: spacing.xSmall_8,
    },
    topHeader: {
        paddingTop: spacing.small_12,
        paddingBottom: spacing.xSmall_8,
    },
    topText: {
        paddingBottom: spacing.medium_16,
    },
    bottomText: {
        paddingTop: spacing.large_24,
    },
    row: {
        flexDirection: "row",
    },
});
